/**
 * Cookie Consent Banner Component
 * GDPR/CCPA compliant consent management
 */

'use client';

import React, { useState, useEffect } from 'react';
import { 
  hasConsent,
  acceptAllConsent,
  rejectNonEssentialConsent,
  saveConsentPreferences
} from '@/utils/analytics/consent';
import { analytics } from '@/utils/analytics';
import type { ConsentPreferences } from '@/utils/analytics/types';

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<Omit<ConsentPreferences, 'timestamp'>>({
    essential: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already given consent
    if (!hasConsent()) {
      setShowBanner(true);
    }
  }, []);

  const handleAcceptAll = () => {
    acceptAllConsent();
    setShowBanner(false);
    
    // Reinitialize analytics after consent
    setTimeout(() => {
      analytics.reinitialize();
    }, 100);
  };

  const handleRejectNonEssential = () => {
    rejectNonEssentialConsent();
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    saveConsentPreferences(preferences);
    setShowBanner(false);
    
    // Reinitialize analytics if analytics consent was granted
    if (preferences.analytics) {
      setTimeout(() => {
        analytics.reinitialize();
      }, 100);
    }
  };

  const togglePreference = (type: keyof typeof preferences) => {
    if (type === 'essential') return; // Essential cookies cannot be disabled
    
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-2xl">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col gap-4">
          {/* Main message */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                🍪 Cookie Preferences
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                We use cookies to enhance your experience, analyze site traffic, and personalize content. 
                By clicking &quot;Accept All&quot;, you consent to our use of cookies.
              </p>
            </div>
          </div>

          {/* Quick actions or detailed preferences */}
          {!showDetails ? (
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={handleRejectNonEssential}
                className="btn btn-ghost btn-sm"
              >
                Essential Only
              </button>
              <button
                onClick={() => setShowDetails(true)}
                className="btn btn-ghost btn-sm"
              >
                Manage Preferences
              </button>
              <button
                onClick={handleAcceptAll}
                className="btn btn-primary btn-sm"
              >
                Accept All
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Detailed preferences */}
              <div className="space-y-3">
                {/* Essential cookies */}
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-sm text-gray-900 dark:text-white">
                      Essential Cookies
                    </h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Required for the website to function properly. Cannot be disabled.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.essential}
                    disabled
                    className="checkbox checkbox-sm"
                  />
                </div>

                {/* Analytics cookies */}
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-sm text-gray-900 dark:text-white">
                      Analytics Cookies
                    </h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Help us understand how visitors interact with our website.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={() => togglePreference('analytics')}
                    className="checkbox checkbox-sm"
                  />
                </div>

                {/* Marketing cookies */}
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-sm text-gray-900 dark:text-white">
                      Marketing Cookies
                    </h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Used to deliver personalized advertisements and measure campaign effectiveness.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={() => togglePreference('marketing')}
                    className="checkbox checkbox-sm"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => setShowDetails(false)}
                  className="btn btn-ghost btn-sm"
                >
                  Back
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="btn btn-primary btn-sm"
                >
                  Save Preferences
                </button>
              </div>
            </div>
          )}

          {/* Privacy policy link */}
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Learn more in our{' '}
            <a href="/privacy" className="underline hover:text-gray-700 dark:hover:text-gray-200">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href="/cookies" className="underline hover:text-gray-700 dark:hover:text-gray-200">
              Cookie Policy
            </a>
            .
          </div>
        </div>
      </div>
    </div>
  );
}